
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { Payslip, Employee } from '@/types';
import { formatDate } from '@/lib/utils';
import { useState } from 'react';
import { DetailView } from '@/components/ui/detail-view';
import { PayslipDetails } from './PayslipDetails';

interface PayslipListProps {
  payslips: Payslip[];
  employees: Employee[];
  onViewPayslip: (payslip: Payslip) => void;
  selectedPayslipId?: string;
  /** Hide employee column when listing payslips for a single employee */
  hideEmployeeColumn?: boolean;
  /** Parent handles detail view (avoids duplicate modals) */
  disableDetailModal?: boolean;
}

export const PayslipList = ({ 
  payslips, 
  employees, 
  onViewPayslip, 
  selectedPayslipId,
  hideEmployeeColumn = false,
  disableDetailModal = false,
}: PayslipListProps) => {
  const [selectedPayslip, setSelectedPayslip] = useState<Payslip | null>(null);
  const [detailViewOpen, setDetailViewOpen] = useState(false);

  // console.log('PayslipList received payslips:', payslips);
  // console.log('PayslipList received employees:', employees);
  // console.log('PayslipList payslips length:', payslips.length);

  const getEmployeeName = (payslip: Payslip) => {
    // First try to use the employee name from the payslip data (from API response)
    if (payslip.employeeName) {
      return payslip.employeeName;
    }
    
    // Fallback to finding employee in the employees array
    const employee = employees.find(e => e.id === payslip.employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = (payslip: Payslip) => {
    setSelectedPayslip(payslip);
    setDetailViewOpen(true);
  };

  return (
    <div>
      <h3 className="font-medium mb-4">Recent Payslips</h3>
      {payslips.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No payslips found
        </div>
      ) : (
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((payslip) => {
                console.log('Rendering payslip:', payslip);
                return (
                  <TableRow 
                    key={payslip.id} 
                    className={selectedPayslipId === payslip.id ? "bg-blue-50" : ""}
                    onClick={() => onViewPayslip(payslip)}
                  >
                    <TableCell className="font-medium">
                      {getEmployeeName(payslip)}
                    </TableCell>
                    <TableCell>{payslip.period || payslip.payPeriod || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(payslip.status)}>
                        {payslip.status.charAt(0).toUpperCase() + payslip.status.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (disableDetailModal) {
                            onViewPayslip(payslip);
                          } else {
                            handleViewDetails(payslip);
                          }
                        }}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!disableDetailModal && selectedPayslip && (
        <DetailView 
          title={`Payslip - ${getEmployeeName(selectedPayslip)} - ${selectedPayslip.payPeriod}`}
          isOpen={detailViewOpen}
          onClose={() => setDetailViewOpen(false)}
          size="xl"
        >
          <PayslipDetails 
            payslip={selectedPayslip}
            employeeName={getEmployeeName(selectedPayslip)}
          />
        </DetailView>
      )}
    </div>
  );
};
